/**
 * A prescription's powers as a small table, one row per eye, the way a prescription form writes them.
 *
 * Spectacle Rx: sphere, then cylinder and axis, the intermediate and near adds, prisms and monocular
 * PDs where any eye has them; a binocular PD goes under the table. Contact lens Rx: power, then cylinder
 * and axis and the add where written, then base curve and diameter. A column no eye uses is left out, so
 * a simple Rx stays narrow. A balance eye is marked; an eye with no contact lens says so.
 *
 * Built when the prescription changes - the store replaces a prescription when it is saved - so the
 * template repeats over the same rows until then.
 */
app.component("lensRxTable", {
	templateUrl: "app/src/js/components/lensPrescribing/lensRxTable.html",
	controller: "lensRxTableController",
	bindings: { prescription: "<" },
});

app.controller("lensRxTableController", [
	"LensPrescribingStore",
	"LensDispensingStore",
	function (Rx, Store) {
		const $ctrl = this;
		const isNum = (v) => v !== null && v !== undefined && v !== "" && !isNaN(v);
		const EYES = [["od", "R"], ["os", "L"]];
		// Whether any of the eyes has a value, other than zero, in a field.
		const any = (eyes, f) => eyes.some((e) => isNum(e[f]) && Number(e[f]) !== 0);
		const cylOf = (e) => (isNum(e.cyl) && Number(e.cyl) !== 0 ? Number(e.cyl) : null);
		const cylColumns = (eyes) => (any(eyes, "cyl") ? [
			// An eye with no cylinder beside one with: written DS, as on a form.
			{ label: "CYL", value: (e) => (cylOf(e) !== null ? Store.fmtPower(e.cyl) : isNum(e.sph) ? "DS" : "") },
			{ label: "AXIS", value: (e) => (cylOf(e) !== null && isNum(e.axis) ? String(e.axis) : "") },
		] : []);
		const table = (columns, eyes, row, note) => ({
			columns: columns.map((c) => c.label),
			rows: EYES.map((k, i) => angular.extend({ eye: k[1], cells: columns.map((c) => c.value(eyes[i])) }, row(eyes[i]))),
			note: note || null,
		});

		function glasses(rx) {
			const eyes = EYES.map((k) => rx[k[0]] || {});
			const monoPd = any(eyes, "pd");
			const columns = [{ label: "SPH", value: (e) => Store.fmtPower(e.sph) }]
				.concat(cylColumns(eyes))
				.concat(any(eyes, "intAdd") ? [{ label: "INT ADD", value: (e) => (Number(e.intAdd) > 0 ? Store.fmtPower(e.intAdd) : "") }] : [])
				.concat(any(eyes, "add") ? [{ label: "ADD", value: (e) => (Number(e.add) > 0 ? Store.fmtPower(e.add) : "") }] : [])
				.concat(eyes.some((e) => Store.fmtPrism(e)) ? [{ label: "PRISM", value: (e) => Store.fmtPrism(e) }] : [])
				.concat(monoPd ? [{ label: "PD", value: (e) => (isNum(e.pd) ? String(e.pd) : "") }] : []);
			const pd = isNum(rx.pd) ? `${monoPd ? "Binocular PD" : "PD"} ${rx.pd} mm` : null;
			return table(columns, eyes, (e) => ({ tag: e.balance ? "Balance" : null }), pd);
		}

		function contacts(p) {
			const eyes = EYES.map((k) => p.cl[k[0]] || {});
			const worn = eyes.filter((e) => !e.noLens);
			// A made-to-order lens's base curves are written to two places, a back toric's flat and steep.
			const custom = !!p.product && !!p.product.custom;
			const bc = (e) => (isNum(e.bc) ? Number(e.bc).toFixed(custom ? 2 : 1) + (custom && isNum(e.bcSteep) ? ` / ${Number(e.bcSteep).toFixed(2)}` : "") : "");
			const columns = [{ label: "POWER", value: (e) => Store.fmtPower(e.sph) }]
				.concat(cylColumns(worn))
				// A contact lens add can be the lens's own - LOW, MED, HIGH, or a power with D or N.
				.concat(worn.some((e) => e.add) ? [{ label: "ADD", value: (e) => (!e.add ? "" : isNum(e.add) ? Store.fmtPower(e.add) : String(e.add)) }] : [])
				.concat(worn.some((e) => isNum(e.bc)) ? [{ label: "BC", value: bc }] : [])
				.concat(worn.some((e) => isNum(e.dia)) ? [{ label: "DIA", value: (e) => (isNum(e.dia) ? Number(e.dia).toFixed(1) : "") }] : []);
			return table(columns, eyes, (e) => ({ none: e.noLens ? "No lens" : null }));
		}

		$ctrl.$onChanges = function () {
			const p = $ctrl.prescription;
			const rx = p && (Rx.isContactsRx(p) ? p.cl : p.rx);
			const written = !!rx && ["od", "os"].some((k) => rx[k] && (isNum(rx[k].sph) || rx[k].balance || rx[k].noLens));
			$ctrl.table = !written ? null : Rx.isContactsRx(p) ? contacts(p) : glasses(rx);
		};
	},
]);
